import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Gift Subscription buy flow (blocks/gift-subscription.liquid). Duration +
 * preference radios; adds each of the preference's wines on the chosen prepaid
 * selling plan, grouped like a bundle (`_case_id` + `_bundle_handle`).
 * Lines/plan ids per preference come from `[data-gift-plans]`.
 *
 * @extends {Component}
 */
class GiftSubscription extends Component {
  /** @typedef {{variant:number, qty:number, plans:Record<string,{id:number, price:number}>}} GiftLine */
  /** @type {Record<string, {available:boolean, lines:GiftLine[]}>} */
  #data = {};

  /** @type {Record<string, number>} plan name → pence */
  #fixedPrices = {};

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#fixedPrices = JSON.parse(this.dataset.fixedPrices || '{}');
    } catch {
      this.#fixedPrices = {};
    }
    try {
      this.#data = JSON.parse(this.querySelector('[data-gift-plans]')?.textContent || '{}');
    } catch (err) {
      console.error('[gift-subscription] bad plan data', err);
    }
    this.update();
  }

  get #duration() {
    return /** @type {HTMLInputElement|null} */ (this.querySelector('input[name^="gift-duration"]:checked'));
  }

  get #preference() {
    return /** @type {HTMLInputElement|null} */ (this.querySelector('input[name^="gift-preference"]:checked'));
  }

  get #selection() {
    const preference = this.#preference;
    const entry = preference ? this.#data[preference.value] : undefined;
    const plan = this.#duration?.value || '';
    const total = entry ? this.#total(entry.lines, plan) : null;
    return { preference, entry, plan, total };
  }

  /**
   * Sum of the bottles' prices on a plan; null if any bottle lacks the plan.
   * @param {GiftLine[]} lines
   * @param {string} plan
   */
  #total(lines, plan) {
    // Fixed gift price for this plan (admin block) wins over the bottle sum.
    const fixed = this.#fixedPrices[plan];
    if (typeof fixed === 'number' && lines.length) return fixed;
    let total = 0;
    for (const line of lines) {
      const price = line.plans[plan]?.price;
      if (price === undefined) return null;
      total += price * line.qty;
    }
    return lines.length ? total : null;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '', this.dataset.currency || 'GBP');
  }

  update() {
    const { entry, total } = this.#selection;
    for (const el of this.querySelectorAll('[data-plan-price]')) {
      const price = entry ? this.#total(entry.lines, /** @type {HTMLElement} */ (el).dataset.planPrice || '') : null;
      if (price !== null) el.textContent = this.#money(price);
    }
    const ctaTotal = this.querySelector('[data-cta-total]');
    if (ctaTotal) ctaTotal.textContent = total !== null ? this.#money(total) : '';
    const cta = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-cta]'));
    if (cta) cta.disabled = total === null || !entry?.available;
  }

  async addToCart() {
    const cta = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-cta]'));
    const { preference, entry, plan, total } = this.#selection;
    if (!cta || cta.disabled || !preference || !entry || total === null) return;

    // Same grouping as bundle adds, so the cart shows one "Gift Subscriptions"
    // row with a qty stepper (new line qty = _per_case_qty × row qty).
    const caseId = `gift-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const items = entry.lines.map((line) => {
      /** @type {Record<string,string>} */
      const properties = {
        _case_id: caseId,
        _bundle_handle: this.dataset.productHandle || '',
        _per_case_qty: String(line.qty),
      };
      return { id: line.variant, quantity: line.qty, selling_plan: line.plans[plan].id, properties };
    });

    cta.disabled = true;
    cta.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(`cart add failed: ${res.status} ${await res.text()}`);
      const cart = await (await fetch('/cart.js')).json();
      document.dispatchEvent(new CartAddEvent(cart, this.id, { itemCount: cart.item_count }));
      /** @type {{open?:()=>void}|null} */ (document.querySelector('cart-drawer-component'))?.open?.();
    } catch (err) {
      console.error('[gift-subscription] add to cart failed', err);
    } finally {
      cta.removeAttribute('aria-busy');
      this.update();
    }
  }
}

if (!customElements.get('gift-subscription')) {
  customElements.define('gift-subscription', GiftSubscription);
}
